import { type ReactNode } from 'react';

export interface ConditionalComponentProps {
  /** Display condition based on empty state */
  displayOnEmptyCondition?: 'Display Only if Not Empty' | 'Display Only if Empty' | null;
  /** Display condition based on login state */
  displayOnLoggedInCondition?: 'Display Only if Logged In' | 'Display Only if Not Logged In' | null;
  /** Whether user is authenticated */
  isAuthenticated?: boolean | null;
  /** Whether the content condition is not empty */
  isNotEmpty?: boolean;
  /** Content to conditionally render */
  children?: ReactNode;
  /** CMS content collection for rendering */
  contentCollection?: {
    items: Array<unknown>;
  } | null;
  /** Render function for CMS content items */
  renderContent?: (items: unknown[]) => ReactNode;
}

/**
 * Conditional component wrapper for CMS content
 *
 * Shows or hides content based on conditions:
 * - Authentication state (logged in / not logged in)
 * - Empty state (content exists / doesn't exist)
 *
 * @example
 * // Show only when logged in
 * <ConditionalComponent
 *   displayOnLoggedInCondition="Display Only if Logged In"
 *   isAuthenticated={user.isLoggedIn}
 * >
 *   <PrivateContent />
 * </ConditionalComponent>
 *
 * @example
 * // Show only when cart is not empty
 * <ConditionalComponent
 *   displayOnEmptyCondition="Display Only if Not Empty"
 *   isNotEmpty={cart.items.length > 0}
 * >
 *   <CartSummary />
 * </ConditionalComponent>
 */
export function ConditionalComponent({
  displayOnEmptyCondition,
  displayOnLoggedInCondition,
  isAuthenticated,
  isNotEmpty,
  children,
  contentCollection,
  renderContent,
}: ConditionalComponentProps) {
  // Evaluate empty condition
  // If not specified, condition passes (true)
  const conditionNotEmptyResult: boolean =
    displayOnEmptyCondition === undefined || displayOnEmptyCondition === null
      ? true
      : isNotEmpty === (displayOnEmptyCondition === 'Display Only if Not Empty');

  // Evaluate logged in condition
  // If not specified, condition passes (true)
  const conditionOnLoggedInResult: boolean =
    displayOnLoggedInCondition === undefined || displayOnLoggedInCondition === null
      ? true
      : !!isAuthenticated === (displayOnLoggedInCondition === 'Display Only if Logged In');

  // Both conditions must pass
  if (!conditionNotEmptyResult || !conditionOnLoggedInResult) {
    return null;
  }

  // If we have CMS content collection and a render function, use it
  if (contentCollection?.items && renderContent) {
    const filteredItems = contentCollection.items.filter(
      (item): item is NonNullable<typeof item> => item !== null && item !== undefined
    );

    if (filteredItems.length === 0) {
      return null;
    }

    return <>{renderContent(filteredItems)}</>;
  }

  // Otherwise render children directly
  return <>{children}</>;
}

ConditionalComponent.displayName = 'ConditionalComponent';
